import { FC, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
	Box,
	Display,
	Divider,
	Flex,
	Modal,
	ModalCloseButton,
	ModalContent,
	ModalHeader,
	ModalOverlay,
	Spacer,
	useBreakpointValue,
	useDisclosure,
} from '@bonusly/design-system';

import { useGratitudeFeedback } from '../../../gratitude-gauge/scorebox-components';
import { useBudget } from '../../hooks/use-budget';
import { AmountChip } from '../amount-chip';
import { FlashPointsChip } from '../flash-points-chip';
import { GiveModalConfirmDelete } from './give-modal-confirm-delete';
import { GiveModalContent, GiveModalContentHandle } from './give-modal-content';

interface GiveModalProps {
	content?: any;
	isOpen: boolean;
	onSuccess: VoidFunction;
	onClose: VoidFunction;
	via?: string;
	motionPreset?: 'slideInBottom' | 'slideInRight' | 'slideInLeft' | 'slideInTop' | 'scale' | 'none';
	portalProps?: any;
	transparentOverlay?: boolean;
}

export const GiveModal: FC<GiveModalProps> = ({ content, isOpen, onClose, onSuccess, via, motionPreset = 'slideInBottom', transparentOverlay = false }) => {
	const { t } = useTranslation(['common', 'recognition']);
	const budget = useBudget();
	const monthlyAllowanceBalanceIsZero = budget.monthlyAllowanceBalance === 0;
	const monthlyAllowanceIsZero = budget.data.allowance === 0;
	const showAmountChip = !(monthlyAllowanceBalanceIsZero && monthlyAllowanceIsZero);
	const giveModalContentRef = useRef<GiveModalContentHandle>(null);
	const [hasVideoAttachment, setHasVideoAttachment] = useState(false);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const { isOpen: isConfirmModalOpen, onClose: onConfirmModalClose, onOpen: onConfirmDeleteVideoModalOpen } = useDisclosure();
	const isMobile = useBreakpointValue({ base: true, sm: false });
	const resetGratitudeFeedback = useGratitudeFeedback((state) => state.reset);
	const handleOnClose = () => {
		// If the user tries to close the modal after attaching a video,
		// we need to let them know doing so will delete the video.
		if (hasVideoAttachment) {
			onConfirmDeleteVideoModalOpen();
		} else {
			onClose();
		}
	};

	const handleCloseComplete = () => {
		resetGratitudeFeedback();
	};

	const handleConfirmDelete = () => {
		onConfirmModalClose();
		onClose();
		setHasVideoAttachment(false);
	};

	const mobileAttributes = {
		height: 'auto',
		minHeight: '100vh',
		m: 0,
		sx: {
			'@supports (-webkit-touch-callout: none)': {
				height: 'auto',
				minHeight: '-webkit-fill-available',
				maxHeight: 'none',
			},
		},
	};
	const desktopAttributes = {
		maxH: 'fit-content',
	};

	const mobileModalHeader = (
		<ModalHeader p={5} position="sticky" top={0} zIndex={1} bg="white" borderBottom="1px solid" borderColor="border.default" height="80px">
			<Flex alignItems="center" justifyContent="space-between" height="100%">
				<ModalCloseButton position="unset" fontSize="md" fontWeight="medium" px={10} py={5} minW={16} bg="transparent" color="secondary.fg">
					{t('cancel', { ns: 'common' })}
				</ModalCloseButton>
				<Display textAlign="center" fontSize="lg" fontWeight="semibold">
					{t('bonus_input.mobile_modal_header', { ns: 'recognition' })}
				</Display>
				<Box minW={16} textAlign="right"></Box>
			</Flex>
		</ModalHeader>
	);
	const desktopModalHeader = (
		<ModalHeader>
			<Flex pr={7}>
				{t('bonus_input.modal_header', { ns: 'recognition' })}
				<Spacer />
				<FlashPointsChip />
				{showAmountChip && <AmountChip amount={budget.monthlyAllowanceBalance ?? 0} h={8} />}
				<ModalCloseButton borderRadius="full" top={4} />
			</Flex>
		</ModalHeader>
	);

	return (
		<>
			<Modal
				data-testid="give-modal"
				isOpen={isOpen}
				onClose={handleOnClose}
				onCloseComplete={handleCloseComplete}
				scrollBehavior="outside"
				size={isMobile ? 'full' : '2xl'}
				motionPreset={motionPreset}
				// Should we disable the overlay click all the time?
				closeOnOverlayClick={!isSubmitting}
			>
				<ModalOverlay
					{...(transparentOverlay ? { bg: 'transparent' } : {})} // Temp Fix for Safari: Ensures modal overlay displays correctly when navigation bar is hidden
					{...(isMobile ? { minH: '200%', bg: 'white' } : {})} // Fix for IOS Keyboard resizing which hides a brief flash of the bonus feed when the keyboard opens
				/>
				<ModalContent {...(isMobile ? mobileAttributes : desktopAttributes)}>
					{isMobile ? mobileModalHeader : desktopModalHeader}
					<Divider />
					<GiveModalContent
						ref={giveModalContentRef}
						content={content}
						onSuccess={onSuccess}
						setIsSubmitting={setIsSubmitting}
						onVideoAttachmentChange={setHasVideoAttachment}
						via={via}
					/>
					{isSubmitting && <Box bgColor="whiteAlpha.700" rounded="md" cursor="not-allowed" inset={0} position="absolute" />}
				</ModalContent>
			</Modal>
			<GiveModalConfirmDelete isOpen={isConfirmModalOpen} onClose={onConfirmModalClose} onConfirm={handleConfirmDelete} />
		</>
	);
};
